import { calculateInterestDeductionRate, isInterestDeductiblePrivateLoan } from "../interest";
import { sum } from "../../../utils";
import { sumLivingExpenses } from "../../../household";
import { sumAdditionalHousingExpenses } from "../housing";
import { calculateYearlyInterestExpense, monthlyInterestExpense } from "../../../interest";
const MONTHS_IN_YEAR = 12;
/**
 * Calculates household monthly gross and net income
 *
 * @param adults Adults parameters
 * @param taxation Income taxation calculations
 * @returns Household gross income and household net income
 */
export function calculateHouseholdSalaryIncome(adults, taxation, taxTable) {
    const grossIncome = sum(adults, (adult) => adult.income);
    const netIncome = sum(adults, (adult) => taxation.netIncome(adult.income, adult.age, taxTable));
    return { grossIncome, netIncome };
}
/**
 * Calculates child benefit. When numer of childen is above what is defined in additional child benefit it is asumed that the additional child
 * benefit is the same as the difference from the last and second benefit defined.
 *
 * @param children Children age and household rate
 * @param childBenefit Child base benefit and multiple children addition
 * @returns Sum of children benefits
 */
export function calculateChildBenefit(children, childBenefit) {
    if (childBenefit === undefined || children.length === 0) {
        return 0;
    }
    const nrOfChildren = children.length;
    const baseChildBenefits = childBenefit.benefitPerChild * nrOfChildren;
    let additionForMultipleChildren = 0;
    switch (nrOfChildren) {
        case 0:
            break;
        case 1:
            break;
        case 2:
            additionForMultipleChildren = childBenefit.additionForMultipleChildren.twoChildren;
            break;
        case 3:
            additionForMultipleChildren = childBenefit.additionForMultipleChildren.threeChildren;
            break;
        case 4:
            additionForMultipleChildren = childBenefit.additionForMultipleChildren.fourChildren;
            break;
        case 5:
            additionForMultipleChildren = childBenefit.additionForMultipleChildren.fiveChildren;
            break;
        default: {
            // More than 5
            const differenceFromFourToFive = childBenefit.additionForMultipleChildren.fiveChildren - childBenefit.additionForMultipleChildren.fourChildren;
            additionForMultipleChildren = childBenefit.additionForMultipleChildren.fiveChildren + differenceFromFourToFive * (nrOfChildren - 5);
        }
    }
    return baseChildBenefits + additionForMultipleChildren;
}
/**
 * Sums household incomes and expenses and discretionary income
 *
 * @param input Forecast input for swedish market
 * @param housingExpenseData Calculated monthly housing expenses
 * @param privateLoansExpenses Calculated private loans expenses
 * @param swedishTaxation Calculation tool for swedish taxes
 * @param localizationParameters Swedish household standard parameters
 * @returns Monthly household gross-, net- and discreationary income and monthly household expense
 */
export function calculateHouseholdAccounts(input, housingExpenseData, privateLoansExpenses, swedishTaxation, localizationParameters) {
    const { grossIncome, netIncome } = calculateHouseholdSalaryIncome(input.adults, swedishTaxation, input.taxTable);
    const { childSupport } = input;
    const childBenefits = calculateChildBenefit(input.children, input.childBenefit);
    const householdIncome = netIncome + childSupport.income + childBenefits;
    const livingExpenses = sumLivingExpenses(input, localizationParameters);
    const privateLoansExpense = sum(privateLoansExpenses, (loan) => loan.amortization + loan.interest);
    const housingExpenses = housingExpenseData.housingExpensesBeforeTaxDeduction;
    const additionalHousingExpenses = sumAdditionalHousingExpenses(housingExpenseData.additionalHousingExpenses);
    const householdExpenses = livingExpenses + privateLoansExpense + housingExpenses + additionalHousingExpenses;
    return {
        grossIncome,
        netIncome,
        householdIncome,
        householdExpenses,
        discreationaryIncome: householdIncome - householdExpenses,
    };
}
/**
 * Sums monthly household discreationary income with custom bank calculated interest rate
 *
 * @param results Full swedish market forecast
 * @param calculationInterestRate Custom bank calculated interest rate
 * @param adults Number of adults in household
 * @param privateLoans Private loans
 * @param localizationParameters Swedish mortgage market regulated parameters
 * @returns Monthly household discreationary income with custom bank calculated interest rate
 */
export function calculateBankDiscreationaryIncome(results, calculationInterestRate, adults, privateLoans, localizationParameters) {
    var _a;
    const mainMortgageInterestExpenseFirstYear = sum(results.slice(0, 12), (monthResult) => Math.round((monthResult.mortgageAmount * calculationInterestRate) / MONTHS_IN_YEAR));
    const additionalMortgageInterestExpenseFirstYear = sum(results.slice(0, 12), (monthResult) => Math.round(((monthResult.additionalMortgageAmount || 0) * calculationInterestRate) / MONTHS_IN_YEAR));
    const privateLoanInterestExpenseFirstYear = sum(privateLoans, (privateLoan) => {
        if (isInterestDeductiblePrivateLoan(privateLoan.type)) {
            const loanInterest = calculateYearlyInterestExpense(privateLoan.amount, privateLoan.amortization, privateLoan.interestRate);
            return loanInterest;
        }
        return 0;
    });
    const interestDeductionRates = calculateInterestDeductionRate({
        yearMainMortgageInterest: mainMortgageInterestExpenseFirstYear,
        yearAdditionalMortgageInterest: additionalMortgageInterestExpenseFirstYear,
        yearPrivateLoanInterest: privateLoanInterestExpenseFirstYear,
    }, adults, localizationParameters);
    const mainMortgageInterestExpenseFirstMonthWithDeduction = Math.round((results[0].mortgageAmount * calculationInterestRate * (1 - interestDeductionRates.mainMortgageInterestDeductionRate)) / MONTHS_IN_YEAR);
    const additionalMortgageInterestExpenseFirstMonthWithDeduction = Math.round((((_a = results[0].additionalMortgageAmount) !== null && _a !== void 0 ? _a : 0) *
        calculationInterestRate *
        (1 - interestDeductionRates.additionalMortgageInterestDeductionRate)) /
        MONTHS_IN_YEAR);
    const privateLoanInterestExpenseFirstMonthWithDeduction = Math.round(sum(privateLoans, (privateLoan) => {
        if (isInterestDeductiblePrivateLoan(privateLoan.type)) {
            return (privateLoan.amount * privateLoan.interestRate) / MONTHS_IN_YEAR;
        }
        return 0;
    }) *
        (1 - interestDeductionRates.privateLoanInterestDeductionRate));
    // Using first month discreationary income with user mortgage interest rate to calculate discreationary income with calculation interest rate
    // All interest expenses from first forecast is removed and replaced with interest expenses with calculated interest rate
    const bankDiscreationaryIncomeFirstMonth = results[0].discreationaryIncome +
        results[0].totalMonthlyInterestAfterDeduction +
        results[0].additionalMortgageInterest +
        sum(results[0].privateLoans, (privateLoanResult) => {
            if (isInterestDeductiblePrivateLoan(privateLoanResult.type)) {
                return privateLoanResult.interest;
            }
            return 0;
        }) -
        mainMortgageInterestExpenseFirstMonthWithDeduction -
        additionalMortgageInterestExpenseFirstMonthWithDeduction -
        privateLoanInterestExpenseFirstMonthWithDeduction;
    return bankDiscreationaryIncomeFirstMonth;
}
/**
 * Calculates costs for private loans
 *
 * @param input Forecast input for swedish market
 * @returns Expenses for each private loan in an array
 */
export function calculatePrivateLoanExpenses(privateLoans) {
    const result = [];
    for (const loan of privateLoans) {
        const installment = Math.min(loan.amount, loan.amortization);
        result.push({
            type: loan.type,
            amount: loan.amount,
            amortization: installment,
            interest: monthlyInterestExpense(loan.amount, loan.interestRate),
        });
    }
    return result;
}
